<script setup lang="ts">
import { computed } from 'vue';
import { BFormCheckbox } from 'bootstrap-vue-next';
import type { RowModel } from './model';
import type { IColumnDefinition, TableOptions } from './types';
import ItpDataTableCell from './ItpDataTableCell.vue';
import { buttonMessages } from '../../messages';

const props = defineProps<{
  row: RowModel;
  columns: IColumnDefinition[];
  options: TableOptions;
}>();

const isEditing = computed(() => props.row.isEditing.value);
const isSelected = computed(() => props.row.isSelected.value);
const isNew = computed(() => props.row.isNew.value);
const isExpanded = computed(() => props.row.isExpanded.value);
const isEditable = computed(() => props.row.isEditable);
const isDeletable = computed(() => props.row.isDeletable);
const showCommands = computed(() => props.options.editable || false);

const onToggleSelect = () => props.row.toggleSelect();
const onToggleExpand = () => props.row.toggleExpand();

const onEdit = () => {
  props.row.beginEdit();
  if (!props.row.isExpanded.value) {
    props.row.toggleExpand();
  }
};
const onCancel = () => {
  props.row.cancelEdit();
  if (props.row.isExpanded.value) {
    props.row.toggleExpand();
  }
};
const onSave = async () => {
  try {
    await props.row.save();
    if (props.row.isExpanded.value) {
      props.row.toggleExpand();
    }
  } catch (e) {
    console.error(e);
  }
};
const onDelete = () => props.row.delete();

const onRowEnter = (event: KeyboardEvent) => {
  if (!isEditing.value) {
    return;
  }
  if (event.defaultPrevented) {
    return;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return;
  }
  if (target.tagName === 'TEXTAREA') {
    return;
  }
  if (
    typeof target.closest === 'function' &&
    target.closest('.mx-datepicker')
  ) {
    return;
  }
  event.preventDefault();
  onSave();
};

const rowClasses = computed(() => ({
  'itp-datatable__datarow': true,
  datarow: true,
  'itp-datatable__row--editing': isEditing.value,
  'table-active': isSelected.value,
}));
</script>

<template>
  <tr :class="rowClasses" @keydown.enter="onRowEnter">
    <td
      v-if="options.rowSelection"
      class="itp-datatable__table-cell itp-datatable__table-row-selector text-center align-middle sticky-l"
      style="width: 20px; min-width: 20px"
      @click.stop
    >
      <BFormCheckbox
        :model-value="isSelected"
        :disabled="isNew"
        @change="onToggleSelect"
      />
    </td>

    <th
      v-if="options.rowHeader"
      scope="row"
      class="itp-datatable__table-cell itp-datatable__row-header text-center align-middle sticky-l"
      :style="{ left: options.rowSelection ? '20px' : '0' }"
      style="width: 20px; min-width: 20px; cursor: pointer"
      @click.stop="onToggleExpand"
    >
      <i
        v-if="
          options.rowDetailsMode === 'expandable' ||
          options.rowDetailsMode === 'alwaysVisible'
        "
        class="text-muted"
        :class="isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
      />
    </th>

    <ItpDataTableCell
      v-for="col in columns"
      :key="col.field"
      :row="row"
      :column="col"
      :editing="isEditing"
    />

    <td
      v-if="options.fillEmptyRowSpace !== false"
      class="itp-datatable__table-cell"
      style="width: 100%"
    />

    <th
      v-if="showCommands"
      scope="row"
      class="itp-datatable__row-edit-commands text-nowrap text-center sticky-r"
      style="width: 80px; min-width: 80px"
    >
      <div v-if="isEditing">
        <button
          type="button"
          class="btn btn-link btn-sm itp-button p-0"
          :title="buttonMessages.save"
          @click.stop="onSave"
        >
          <i class="fas fa-check text-success" />
        </button>
        <button
          type="button"
          class="btn btn-link btn-sm itp-button p-0"
          :title="buttonMessages.cancel"
          @click.stop="onCancel"
        >
          <i class="fas fa-times text-danger" />
        </button>
      </div>

      <div v-else class="default-commands">
        <button
          v-if="isEditable"
          type="button"
          class="btn btn-link btn-sm itp-button text-nowrap p-0"
          :title="buttonMessages.edit"
          @click.stop="onEdit"
        >
          <i class="fas fa-pen" />
        </button>

        <button
          v-if="isDeletable"
          type="button"
          class="btn btn-link btn-sm itp-button text-nowrap p-0"
          :title="buttonMessages.delete"
          @click.stop="onDelete"
        >
          <i class="fas fa-trash" />
        </button>
      </div>
    </th>
  </tr>
</template>
